import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Stack, Typography, Avatar, IconButton, Tooltip, CircularProgress, Menu, MenuItem,
    Popover, InputBase,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import toast from 'react-hot-toast';
import http, { apiErrorMessage } from '../../Api/http';
import {
    FetchMessages, SendMessage, DeleteChatMessage, EditMessage, ReactMessage, PinMessage,
    MarkRead, ClearChat, LeaveGroup, DeleteGroup, SearchMessages, GetMessageReadInfo, GetGroupInfo,
} from '../../Api/Api';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { EmptyState, ConfirmDialog } from '../uiKit';
import ChatLoader from './ChatLoader';
import {
    avatarToneFor, initialsFrom, classifyFile, linkify,
    formatMessageTime, formatDayDivider, canEditMessage, systemEventLabel,
    copyTextToClipboard, QUICK_REACTION_EMOJIS, extractMessageList,
} from './chatHelpers';
import AudioMessage from './AudioMessage';
import MessageComposer from './MessageComposer';
import GroupInfoPanel from './GroupInfoPanel';
import ManageMembersDialog from './ManageMembersDialog';
import EmojiPickerPanel from './EmojiPickerPanel';
import {
    getChatConnection, onChatConnectionReady, setActiveChatGroupId, markChatGroupRead, onChatReconnected,
} from './chatSocket';

const QUICK_REACTIONS = QUICK_REACTION_EMOJIS;
const PAGE_SIZE = 50;

// Module-level (not component state), so it survives this component's own
// remounts — ChatPage renders <MessageThread key={selectedGroupId} />, which
// fully unmounts/remounts on every group switch. Without this, reopening a
// group you already viewed this session waits on the same FetchMessages
// round trip again before anything shows. Caching the last-seen page here
// lets that reopen render instantly from memory while a background refetch
// (the effect below) reconciles it with the server.
const messageCache = new Map();

const upsertMessage = (list, msg) => {
    const idx = list.findIndex((m) => m.messageId === msg.messageId);
    if (idx === -1) return [...list, msg].sort((a, b) => a.messageId - b.messageId);
    const next = list.slice();
    next[idx] = { ...next[idx], ...msg };
    return next;
};

// How far a bubble has to be dragged (in px) before release counts as a
// reply, mirroring the swipe-to-reply gesture from WhatsApp/Telegram — mine
// swipes left, others' swipe right, and either way it snaps back afterwards
// regardless of whether it triggered a reply.
const REPLY_DRAG_THRESHOLD = 56;

function SwipeToReplyRow({ mine, disabled, onReply, children }) {
    const x = useMotionValue(0);
    const iconOpacity = useTransform(
        x,
        mine ? [-REPLY_DRAG_THRESHOLD, 0] : [0, REPLY_DRAG_THRESHOLD],
        mine ? [1, 0] : [0, 1],
    );
    const triggeredRef = useRef(false);

    if (disabled) return children;

    return (
        <Box sx={{ position: 'relative' }}>
            <motion.div
                style={{
                    position: 'absolute', top: '50%', marginTop: -9, pointerEvents: 'none',
                    opacity: iconOpacity, [mine ? 'right' : 'left']: -26,
                }}
            >
                <ReplyRoundedIcon sx={{ fontSize: 18, color: PRIMARY }} />
            </motion.div>
            <motion.div
                drag="x"
                style={{ x, touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none' }}
                dragConstraints={mine ? { left: -80, right: 0 } : { left: 0, right: 80 }}
                dragElastic={0.5}
                dragSnapToOrigin
                onDrag={(_, info) => {
                    if (!triggeredRef.current && Math.abs(info.offset.x) > REPLY_DRAG_THRESHOLD) triggeredRef.current = true;
                }}
                onDragEnd={() => {
                    if (triggeredRef.current) onReply();
                    triggeredRef.current = false;
                }}
            >
                {children}
            </motion.div>
        </Box>
    );
}

function Attachment({ message, mine }) {
    const kind = classifyFile(message.attachmentName || message.attachmentUrl);
    if (kind.category === 'image') {
        return (
            <Box component="a" href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', mb: message.messageText ? 0.8 : 0 }}>
                <Box component="img" src={message.attachmentUrl} alt={message.attachmentName || ''} sx={{ maxWidth: 220, maxHeight: 220, borderRadius: '9px', display: 'block' }} />
            </Box>
        );
    }
    if (kind.category === 'video') {
        return (
            <Box sx={{ mb: message.messageText ? 0.8 : 0 }}>
                <Box component="video" src={message.attachmentUrl} controls sx={{ maxWidth: 240, maxHeight: 220, borderRadius: '9px', display: 'block' }} />
            </Box>
        );
    }
    if (kind.category === 'audio') {
        return <Box sx={{ mb: message.messageText ? 0.8 : 0 }}><AudioMessage src={message.attachmentUrl} mine={mine} accent={PRIMARY} /></Box>;
    }
    return (
        <Stack
            direction="row" spacing={1} component="a" href={message.attachmentUrl} target="_blank" rel="noopener noreferrer"
            sx={{ alignItems: 'center', textDecoration: 'none', mb: message.messageText ? 0.8 : 0, bgcolor: mine ? 'rgba(255,255,255,0.14)' : '#E4F0F9', borderRadius: '9px', px: 1.2, py: 0.9, minWidth: 180 }}
        >
            <InsertDriveFileRoundedIcon sx={{ fontSize: 20, color: mine ? '#fff' : PRIMARY }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: mine ? '#fff' : '#0F172A' }} noWrap>{message.attachmentName || 'Attachment'}</Typography>
            </Box>
            <DownloadRoundedIcon sx={{ fontSize: 16, color: mine ? 'rgba(255,255,255,0.8)' : '#94A3B8' }} />
        </Stack>
    );
}

export default function MessageThread({
    groupId, groupSummary, myUserId, isMasterAdmin, isEntityAdmin, isMobile,
    onBack, onGroupsListChanged, onGroupRemoved, onLastMessageSeen,
}) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [groupInfo, setGroupInfo] = useState(null);
    const [typingUsers, setTypingUsers] = useState(() => new Map());
    const [readByAllUpTo, setReadByAllUpTo] = useState(0);
    const [pinnedMessage, setPinnedMessage] = useState(null);

    const [replyTo, setReplyTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [sending, setSending] = useState(false);

    // Multi-select delete — WhatsApp-style: pick "Select" on one message to
    // enter the mode (already selected), tap others to add/remove, then bulk
    // delete everything picked in one go instead of one-by-one.
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

    const [infoOpen, setInfoOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const [reactAnchor, setReactAnchor] = useState(null); // { anchorEl, message }
    const [reactMoreOpen, setReactMoreOpen] = useState(false);
    const [whoReacted, setWhoReacted] = useState(null); // { anchorEl, reaction, message }
    const [readInfoAnchor, setReadInfoAnchor] = useState(null);
    const [readInfo, setReadInfo] = useState(null);

    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchIndex, setSearchIndex] = useState(0);
    const [highlightMsgId, setHighlightMsgId] = useState(null);
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [newBelowCount, setNewBelowCount] = useState(0);

    const scrollRef = useRef(null);
    const unreadDividerIdRef = useRef(null);
    const markedUpToRef = useRef(0);
    const lastTypingSentRef = useRef(0);
    const typingTimersRef = useRef(new Map());
    const nearBottomRef = useRef(true);
    const prevNewestIdRef = useRef(0);
    const highlightTimerRef = useRef(null);

    // ---- initial load ----
    useEffect(() => {
        if (!groupId) return undefined;
        // Paint whatever we already have for this group (see messageCache
        // above) immediately, so reopening a recently-viewed conversation
        // doesn't sit behind a spinner while the fetch below — which still
        // always runs, to reconcile with the server — is in flight.
        const cached = messageCache.get(groupId) || [];
        setMessages(cached); setLoading(cached.length === 0); setHasMoreOlder(true);
        setGroupInfo(null); setPinnedMessage(cached.find((m) => m.isPinned) || null); setReadByAllUpTo(0);
        unreadDividerIdRef.current = null; markedUpToRef.current = 0;
        setReplyTo(null); setEditingMessage(null); setSearchOpen(false);
        setShowScrollDown(false); setNewBelowCount(0); setHighlightMsgId(null);
        nearBottomRef.current = true;
        prevNewestIdRef.current = cached.length ? cached[cached.length - 1].messageId : 0;

        let cancelled = false;
        http.get(FetchMessages, { params: { groupId, pageSize: PAGE_SIZE } })
            .then(({ data: body }) => {
                if (cancelled) return;
                if (body?.error) throw new Error(body.message);
                const list = extractMessageList(body);
                const sorted = list.slice().sort((a, b) => a.messageId - b.messageId);
                setMessages(sorted);
                setHasMoreOlder(sorted.length >= PAGE_SIZE);
                const pinned = sorted.find((m) => m.isPinned);
                if (pinned) setPinnedMessage(pinned);
                // The sidebar's own GetGroups summary can be stale/incomplete
                // for a group's last-message preview (e.g. it never got
                // updated for some reason) — since we just fetched this
                // group's real, authoritative latest messages directly, use
                // that to correct the sidebar instead of trusting the
                // summary blindly.
                if (sorted.length) onLastMessageSeen?.(groupId, sorted[sorted.length - 1]);
                const unread = groupSummary?.unreadCount || 0;
                if (unread > 0 && sorted.length) {
                    const idx = Math.max(0, sorted.length - unread);
                    unreadDividerIdRef.current = sorted[idx]?.messageId ?? null;
                }
            })
            .catch((err) => toast.error(apiErrorMessage(err, 'Could not load messages.')))
            .finally(() => { if (!cancelled) setLoading(false); });

        http.get(GetGroupInfo, { params: { groupId } })
            .then(({ data: body }) => { if (!cancelled && !body?.error) setGroupInfo(body?.data || null); })
            .catch(() => {});

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);

    // Keeps messageCache current with every in-session change (sends,
    // receives, edits, deletes, reactions, pins, older-page loads, …) so
    // reopening this group later — even after those changes — starts from
    // an up-to-date snapshot instead of whatever the last fetch above saw.
    useEffect(() => {
        if (groupId && messages.length) messageCache.set(groupId, messages);
    }, [groupId, messages]);

    // Initial jump-to-bottom once the first page for this group has loaded.
    useEffect(() => {
        const el = scrollRef.current;
        if (el && !loading) {
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
                nearBottomRef.current = true;
                setShowScrollDown(false);
                setNewBelowCount(0);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, groupId]);

    // Keep the thread "stuck" to the newest message as new ones arrive at the
    // tail (sent or received) while the user is already near the bottom —
    // mirrors schoolmate's stay-at-bottom-unless-scrolled-up behavior. Keyed
    // off the newest message id (not messages.length) so a prepend from
    // loadOlder — which changes length but never the tail — never fights
    // loadOlder's own scroll-position preservation below.
    const newestIdForScroll = messages.length ? messages[messages.length - 1].messageId : 0;
    useEffect(() => {
        if (!newestIdForScroll || loading) return;
        const prev = prevNewestIdRef.current;
        prevNewestIdRef.current = newestIdForScroll;
        if (!prev || newestIdForScroll <= prev) return; // first paint for this group, or not a tail append
        const last = messages[messages.length - 1];
        const mine = last?.senderUserId != null && last.senderUserId === myUserId;
        if (nearBottomRef.current || mine) {
            const el = scrollRef.current;
            requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight; });
            setNewBelowCount(0);
        } else {
            setNewBelowCount((c) => c + 1);
            setShowScrollDown(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newestIdForScroll, loading]);

    useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

    // ---- hub wiring: this component adds its OWN handlers on top of the
    // baseline ones chatSocket.js already registered — connection.on()
    // supports multiple handlers per event, so this never touches the
    // connection's lifecycle (see useChatSocket.js at DashboardLayout). ----
    useEffect(() => {
        let cancelled = false;
        let unsub = () => {};
        let unsubReconnect = () => {};
        // Captured once — the ref itself is never reassigned for the life of
        // this component, only the Map's contents mutate, so reading
        // .current here (rather than inside the cleanup below) keeps this
        // effect's cleanup pointed at the exact same Map instance.
        const timers = typingTimersRef.current;

        setActiveChatGroupId(groupId);

        // Doc §6: on reconnect, catch up the currently-open group via
        // FetchMessages?after=<lastKnownMessageId> — never a full reload,
        // which would wipe already-loaded scrollback. Subscribed through
        // chatSocket's own listener set (not connection.onreconnected(...)
        // directly, which has no "off" and would leak a new callback every
        // time this component remounts for a different group — see
        // chatSocket.js). Cleaned up below on unmount/group change.
        const catchUp = () => {
            setMessages((list) => {
                const last = [...list].reverse().find((m) => !m.isTemp);
                if (!last) return list;
                http.get(FetchMessages, { params: { groupId, after: last.messageId, pageSize: PAGE_SIZE } })
                    .then(({ data: body }) => {
                        if (body?.error) return;
                        const items = extractMessageList(body);
                        if (items.length) setMessages((cur) => items.reduce((acc, m) => upsertMessage(acc, m), cur));
                    })
                    .catch(() => {});
                return list;
            });
        };
        {
            const attach = (c) => {
                if (cancelled || !c) return;
                const conn = c;

                const onReceive = (msg) => {
                    if (msg?.groupId !== groupId) return;
                    setMessages((list) => upsertMessage(list, msg));
                    if (msg.isPinned) setPinnedMessage(msg);
                };
                const onEdited = (msg) => { if (msg?.groupId === groupId) setMessages((list) => upsertMessage(list, msg)); };
                const onDeleted = (p) => {
                    if (p?.groupId !== groupId) return;
                    setMessages((list) => list.map((m) => (m.messageId === p.messageId ? { ...m, isDeleted: true } : m)));
                };
                const onPinned = (p) => {
                    if (p?.groupId !== groupId) return;
                    setMessages((list) => list.map((m) => (m.messageId === p.messageId ? { ...m, isPinned: p.isPinned, pinnedByUserId: p.pinnedByUserId, pinnedOn: p.pinnedOn } : m)));
                    if (p.isPinned) {
                        setMessages((list) => {
                            const found = list.find((m) => m.messageId === p.messageId);
                            if (found) setPinnedMessage({ ...found, isPinned: true });
                            return list;
                        });
                    } else {
                        setPinnedMessage((cur) => (cur?.messageId === p.messageId ? null : cur));
                    }
                };
                const onReacted = (p) => {
                    if (p?.groupId !== groupId) return;
                    setMessages((list) => list.map((m) => (m.messageId === p.messageId ? { ...m, reactions: p.reactions || [] } : m)));
                };
                const onRead = (p) => {
                    if (p?.groupId !== groupId) return;
                    if (p.readByAllUpToMessageId) setReadByAllUpTo((cur) => Math.max(cur, p.readByAllUpToMessageId));
                };
                const onTyping = (p) => {
                    if (p?.groupId !== groupId || p.userId === myUserId) return;
                    setTypingUsers((map) => {
                        const next = new Map(map);
                        next.set(p.userId, p.name);
                        return next;
                    });
                    if (timers.has(p.userId)) clearTimeout(timers.get(p.userId));
                    timers.set(p.userId, setTimeout(() => {
                        setTypingUsers((map) => {
                            const next = new Map(map);
                            next.delete(p.userId);
                            return next;
                        });
                        timers.delete(p.userId);
                    }, 4000));
                };
                const onGroupUpdated = (p) => {
                    if (p?.groupId !== groupId) return;
                    http.get(GetGroupInfo, { params: { groupId } }).then(({ data: body }) => { if (!body?.error) setGroupInfo(body?.data || null); }).catch(() => {});
                    onGroupsListChanged?.();
                };
                const onMemberRoleChanged = (p) => {
                    // Doc §5.2: update the member's role badge, and if it's
                    // the current user, the admin-only actions this screen
                    // exposes — refetching GetGroupInfo covers both, since
                    // amGroupAdmin/canManageGroup are derived from it.
                    if (p?.groupId !== groupId) return;
                    http.get(GetGroupInfo, { params: { groupId } }).then(({ data: body }) => { if (!body?.error) setGroupInfo(body?.data || null); }).catch(() => {});
                };
                const onGroupDeleted = (p) => {
                    if (p?.groupId !== groupId) return;
                    toast('This group no longer exists.');
                    onGroupRemoved?.(groupId);
                };
                const onRemovedFromGroup = (p) => {
                    if (p?.groupId !== groupId) return;
                    toast('You are no longer a member of this group.');
                    setGroupInfo((info) => (info ? { ...info, hasLeft: true } : info));
                };
                const onChatCleared = (p) => {
                    if (p?.groupId !== groupId) return;
                    setMessages((list) => list.filter((m) => m.messageId > (p.clearedUpToMessageId || 0)));
                };

                conn.on('ReceiveMessage', onReceive);
                conn.on('MessageEdited', onEdited);
                conn.on('MessageDeleted', onDeleted);
                conn.on('MessagePinned', onPinned);
                conn.on('MessageReacted', onReacted);
                conn.on('MessageRead', onRead);
                conn.on('Typing', onTyping);
                conn.on('GroupUpdated', onGroupUpdated);
                conn.on('GroupDeleted', onGroupDeleted);
                conn.on('RemovedFromGroup', onRemovedFromGroup);
                conn.on('ChatCleared', onChatCleared);
                conn.on('MemberRoleChanged', onMemberRoleChanged);

                unsub = () => {
                    conn.off('ReceiveMessage', onReceive);
                    conn.off('MessageEdited', onEdited);
                    conn.off('MessageDeleted', onDeleted);
                    conn.off('MessagePinned', onPinned);
                    conn.off('MessageReacted', onReacted);
                    conn.off('MessageRead', onRead);
                    conn.off('Typing', onTyping);
                    conn.off('GroupUpdated', onGroupUpdated);
                    conn.off('GroupDeleted', onGroupDeleted);
                    conn.off('RemovedFromGroup', onRemovedFromGroup);
                    conn.off('ChatCleared', onChatCleared);
                    conn.off('MemberRoleChanged', onMemberRoleChanged);
                };
            };

            // attach()/onChatConnectionReady() each return their own
            // unsubscribe function — both were previously being called for
            // their side effects only, with the returned cleanup discarded,
            // so conn.off(...) never actually ran on unmount/group-switch and
            // every previously-opened group's handlers stayed attached to the
            // singleton connection forever. Capture whichever applies so the
            // real detach function (not just the wait-for-ready canceller)
            // ends up in `unsub` once attach actually runs.
            //
            // Wrapped in a function (not just run once inline) because the
            // singleton connection can now be a brand-new object after this
            // component mounted — e.g. the tab lost and regained focus,
            // tearing the hub connection down and back up for presence
            // purposes (see chatSocket.js module comment) while this
            // MessageThread stayed mounted the whole time. Re-running this
            // on every "(re)connected" notification (see unsubReconnect
            // below) re-attaches to whichever connection is current instead
            // of leaving these handlers registered on a dead one.
            const attachNow = () => {
                unsub();
                const existing = getChatConnection();
                if (existing) {
                    unsub = attach(existing) || (() => {});
                } else {
                    unsub = onChatConnectionReady((c) => {
                        unsub = attach(c) || (() => {});
                    });
                }
            };
            attachNow();
            unsubReconnect = onChatReconnected(() => { catchUp(); attachNow(); });
        }

        return () => {
            cancelled = true;
            unsub();
            unsubReconnect();
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
            setActiveChatGroupId(null);
            markChatGroupRead(groupId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, myUserId]);

    // ---- mark-read on scroll-into-view (approximated: whenever the newest
    // message changes while the pane is scrolled near the bottom) ----
    // Skips any not-yet-confirmed optimistic message — its messageId is a
    // local placeholder, not one the server (or MarkRead) knows about.
    const lastRealMessage = messages.length ? [...messages].reverse().find((m) => !m.isTemp) : null;
    const newestId = lastRealMessage ? lastRealMessage.messageId : 0;
    useEffect(() => {
        if (!newestId || newestId <= markedUpToRef.current) return;
        const el = scrollRef.current;
        const nearBottom = !el || (el.scrollHeight - el.scrollTop - el.clientHeight < 200);
        if (!nearBottom) return;
        markedUpToRef.current = newestId;
        http.put(MarkRead, { GroupId: groupId, LastReadMessageId: newestId }).catch(() => {});
        markChatGroupRead(groupId);
        onGroupsListChanged?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newestId, groupId]);

    const loadOlder = useCallback(() => {
        if (loadingOlder || !hasMoreOlder || !messages.length) return;
        setLoadingOlder(true);
        const el = scrollRef.current;
        const prevHeight = el ? el.scrollHeight : 0;
        const oldestId = messages[0].messageId;
        http.get(FetchMessages, { params: { groupId, before: oldestId, pageSize: PAGE_SIZE } })
            .then(({ data: body }) => {
                if (body?.error) throw new Error(body.message);
                const items = extractMessageList(body);
                setHasMoreOlder(items.length >= PAGE_SIZE);
                if (items.length) {
                    setMessages((list) => items.slice().sort((a, b) => a.messageId - b.messageId).concat(list));
                    requestAnimationFrame(() => {
                        if (el) el.scrollTop = el.scrollHeight - prevHeight;
                    });
                }
            })
            .catch(() => {})
            .finally(() => setLoadingOlder(false));
    }, [groupId, loadingOlder, hasMoreOlder, messages]);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollTop < 80) loadOlder();
        const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
        nearBottomRef.current = distance < 150;
        setShowScrollDown(distance > 250);
        if (distance < 150) setNewBelowCount(0);
    };

    const scrollToBottomNow = () => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        nearBottomRef.current = true;
        setShowScrollDown(false);
        setNewBelowCount(0);
    };

    // ---- actions ----
    const handleTyping = () => {
        const now = Date.now();
        if (now - lastTypingSentRef.current < 2000) return;
        lastTypingSentRef.current = now;
        getChatConnection()?.invoke('Typing', groupId).catch(() => {});
    };

    const sendMessage = async (text, file) => {
        setSending(true);
        // Show the message immediately with the device's own current time
        // instead of waiting on the round trip — SendMessage's response
        // (and the sentOn it carries) can lag behind the moment the user
        // actually hit send, especially on a slow connection. Uses a
        // timestamp well above any real (small, sequential) messageId so
        // the "stay at bottom" / mark-read effects below, which key off
        // messageId ordering, still treat this as the newest message.
        const tempId = Date.now();
        const attachmentPreviewUrl = file ? URL.createObjectURL(file) : null;
        const optimisticMsg = {
            messageId: tempId,
            groupId,
            senderUserId: myUserId,
            senderName: myMember?.name || 'You',
            messageText: text || '',
            attachmentUrl: attachmentPreviewUrl,
            attachmentName: file ? file.name : null,
            sentOn: new Date(),
            replyToMessageId: replyTo?.messageId ?? null,
            replyToSenderName: replyTo?.senderName ?? null,
            replyToText: replyTo?.messageText ?? null,
            messageType: 'text',
            isDeleted: false,
            isEdited: false,
            isPinned: false,
            reactions: [],
            isTemp: true,
        };
        setMessages((list) => [...list, optimisticMsg]);
        setReplyTo(null);
        // Update the sidebar's preview/time right away too, for the same
        // reason as the bubble above — otherwise it sits on the previous
        // message until the server round trip finishes.
        onLastMessageSeen?.(groupId, optimisticMsg);
        try {
            const fd = new FormData();
            fd.append('GroupId', groupId);
            if (text) fd.append('MessageText', text);
            if (optimisticMsg.replyToMessageId) fd.append('ReplyToMessageId', optimisticMsg.replyToMessageId);
            if (file) fd.append('File', file);
            const { data: body } = await http.post(SendMessage, fd);
            if (body?.error) throw new Error(body.message || 'Could not send the message.');
            // Keep showing the send time the optimistic bubble already
            // displayed rather than swapping in the server's own sentOn —
            // otherwise the timestamp visibly jumps the instant the real
            // message replaces the temp one (server clock/round-trip lag
            // can put it a little behind the moment the user actually hit
            // send). Everything else (messageId, attachment URL, etc.)
            // still comes from the server.
            const reconciled = body?.data ? { ...body.data, sentOn: optimisticMsg.sentOn } : null;
            setMessages((list) => {
                const withoutTemp = list.filter((m) => m.messageId !== tempId);
                return reconciled ? upsertMessage(withoutTemp, reconciled) : withoutTemp;
            });
            if (reconciled) {
                // The hub's ReceiveMessage broadcast goes to every OTHER
                // group member, never back to the sender — so without this,
                // your own sidebar preview never updates live for a message
                // you just sent; it would only catch up whenever the next
                // incidental GetGroups refetch happens to occur.
                onLastMessageSeen?.(groupId, reconciled);
            }
            onGroupsListChanged?.();
        } catch (err) {
            setMessages((list) => list.filter((m) => m.messageId !== tempId));
            toast.error(apiErrorMessage(err, 'Could not send the message.'));
        } finally {
            if (attachmentPreviewUrl) URL.revokeObjectURL(attachmentPreviewUrl);
            setSending(false);
        }
    };

    const saveEdit = async (text) => {
        setSending(true);
        try {
            const { data: body } = await http.put(EditMessage, { MessageId: editingMessage.messageId, MessageText: text });
            if (body?.error) throw new Error(body.message || 'Could not edit the message.');
            if (body?.data) setMessages((list) => upsertMessage(list, body.data));
            setEditingMessage(null);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not edit the message.'));
        } finally {
            setSending(false);
        }
    };

    const deleteMessage = async (message) => {
        try {
            const { data: body } = await http.put(DeleteChatMessage, { MessageId: message.messageId });
            if (body?.error) throw new Error(body.message || 'Could not delete the message.');
            setMessages((list) => list.map((m) => (m.messageId === message.messageId ? { ...m, isDeleted: true } : m)));
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not delete the message.'));
        }
    };

    const enterSelectMode = (messageId) => {
        setSelectMode(true);
        setSelectedIds(new Set([messageId]));
    };

    const exitSelectMode = () => {
        setSelectMode(false);
        setSelectedIds(new Set());
    };

    const toggleSelected = (messageId) => {
        setSelectedIds((cur) => {
            const next = new Set(cur);
            if (next.has(messageId)) next.delete(messageId); else next.add(messageId);
            return next;
        });
    };

    // DeleteChatMessage only takes one MessageId at a time, so a bulk delete
    // is just that same call fired for every selected message — each one
    // judged by the same mine-or-canManageGroup rule the per-message Delete
    // menu item already uses, in case selection picked up something the
    // current user actually can't delete.
    const bulkDeleteSelected = async () => {
        setConfirmBulkDelete(false);
        const selected = messages.filter((m) => selectedIds.has(m.messageId));
        // A selection can include messages that are already "This message
        // was deleted" tombstones (selectable so they can be cleared out —
        // see MessageTools). There's nothing left to delete server-side for
        // those, so instead of silently no-op'ing (which looked like the
        // confirm did nothing at all), just drop them from view here.
        const alreadyDeletedIds = new Set(selected.filter((m) => m.isDeleted).map((m) => m.messageId));
        const targets = selected.filter((m) => !m.isDeleted && (m.senderUserId === myUserId || canManageGroup));

        let deletedIds = new Set();
        if (targets.length) {
            const results = await Promise.allSettled(
                targets.map((m) => http.put(DeleteChatMessage, { MessageId: m.messageId })),
            );
            deletedIds = new Set();
            results.forEach((r, i) => {
                if (r.status === 'fulfilled' && !r.value?.data?.error) deletedIds.add(targets[i].messageId);
            });
            if (deletedIds.size < targets.length) toast.error('Some messages could not be deleted.');
        }

        if (deletedIds.size || alreadyDeletedIds.size) {
            setMessages((list) => list
                .filter((m) => !alreadyDeletedIds.has(m.messageId))
                .map((m) => (deletedIds.has(m.messageId) ? { ...m, isDeleted: true } : m)));
        }
        exitSelectMode();
    };

    const togglePin = async (message) => {
        try {
            const nextPin = !message.isPinned;
            const { data: body } = await http.put(PinMessage, { MessageId: message.messageId, Pin: nextPin });
            if (body?.error) throw new Error(body.message || 'Could not update the pin.');
            setMessages((list) => list.map((m) => (m.messageId === message.messageId ? { ...m, isPinned: nextPin } : m)));
            setPinnedMessage(nextPin ? { ...message, isPinned: true } : null);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not update the pin.'));
        }
    };

    const react = async (message, emoji) => {
        setReactAnchor(null);
        setReactMoreOpen(false);
        const mine = message.reactions?.find((r) => r.userIds?.includes(myUserId));
        const nextEmoji = mine?.emoji === emoji ? null : emoji;
        try {
            const { data: body } = await http.put(ReactMessage, { MessageId: message.messageId, Emoji: nextEmoji });
            if (body?.error) throw new Error(body.message);
            if (body?.data?.reactions) setMessages((list) => list.map((m) => (m.messageId === message.messageId ? { ...m, reactions: body.data.reactions } : m)));
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not react to the message.'));
        }
    };

    const openReadInfo = (e, message) => {
        setReadInfoAnchor(e.currentTarget);
        setReadInfo({ loading: true });
        http.get(GetMessageReadInfo, { params: { messageId: message.messageId } })
            .then(({ data: body }) => setReadInfo({ loading: false, data: body?.data || null }))
            .catch(() => setReadInfo({ loading: false, data: null }));
    };

    const runSearch = async (query) => {
        setSearchQuery(query);
        if (!query.trim()) { setSearchResults([]); return; }
        try {
            const { data: body } = await http.get(SearchMessages, { params: { groupId, query: query.trim(), pageSize: 30 } });
            if (body?.error) throw new Error(body.message);
            const items = extractMessageList(body);
            setSearchResults(items);
            setSearchIndex(0);
            if (items.length) jumpToMessageId(items[0].messageId);
        } catch {
            setSearchResults([]);
        }
    };

    const flashHighlight = (messageId) => {
        setHighlightMsgId(messageId);
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightMsgId(null), 1600);
    };

    /** Scrolls to a message already in the loaded window; if it isn't loaded
     * (a search hit further back than the current scrollback), fetches a
     * window of context around it via FetchMessages before/after and merges
     * it into the already-loaded messages (keeping the live tail intact)
     * before scrolling — mirrors schoolmate's jumpToMessage. */
    const jumpToMessageId = async (messageId) => {
        const existing = document.getElementById(`chat-msg-${messageId}`);
        if (existing) {
            existing.scrollIntoView({ block: 'center', behavior: 'smooth' });
            flashHighlight(messageId);
            return;
        }
        try {
            const [olderRes, newerRes] = await Promise.all([
                http.get(FetchMessages, { params: { groupId, before: messageId + 1, pageSize: 25 } }),
                http.get(FetchMessages, { params: { groupId, after: messageId, pageSize: 25 } }),
            ]);
            const olderItems = extractMessageList(olderRes.data);
            const newerItems = extractMessageList(newerRes.data);
            if (olderItems.length >= 25) setHasMoreOlder(true);
            setMessages((list) => [...olderItems, ...newerItems].reduce((acc, m) => upsertMessage(acc, m), list));
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const el = document.getElementById(`chat-msg-${messageId}`);
                    if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); flashHighlight(messageId); }
                    else toast('Could not locate that message.');
                });
            });
        } catch {
            toast.error('Could not load that part of the conversation.');
        }
    };

    const jumpToSearchResult = (dir) => {
        if (!searchResults.length) return;
        const next = (searchIndex + dir + searchResults.length) % searchResults.length;
        setSearchIndex(next);
        jumpToMessageId(searchResults[next].messageId);
    };

    const copyMessageText = async (message) => {
        const text = message?.messageText || '';
        if (!text) return;
        const ok = await copyTextToClipboard(text);
        if (ok) toast.success('Copied to clipboard.');
        else toast.error('Could not copy the message.');
    };

    const clearChat = async () => {
        try {
            const { data: body } = await http.put(ClearChat, { GroupId: groupId });
            if (body?.error) throw new Error(body.message);
            const upTo = body?.data?.clearedUpToMessageId ?? (messages.length ? messages[messages.length - 1].messageId : 0);
            setMessages((list) => list.filter((m) => m.messageId > upTo));
            toast.success('Chat cleared.');
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not clear the chat.'));
        }
    };

    const leaveGroup = async () => {
        try {
            const { data: body } = await http.put(LeaveGroup, { GroupId: groupId });
            if (body?.error) throw new Error(body.message);
            toast.success('You left the group.');
            onGroupRemoved?.(groupId);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not leave the group.'));
        }
    };

    const deleteGroup = async () => {
        try {
            const { data: body } = await http.put(DeleteGroup, { GroupId: groupId });
            if (body?.error) throw new Error(body.message);
            toast.success('Group deleted.');
            onGroupRemoved?.(groupId);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not delete the group.'));
        }
    };

    // ---- derived ----
    const members = useMemo(() => (Array.isArray(groupInfo?.members) ? groupInfo.members : []), [groupInfo]);
    const myMember = members.find((m) => m.userId === myUserId);
    const amGroupAdmin = Boolean(myMember?.role === 'admin' || groupInfo?.isCreator || groupInfo?.myRole === 'admin');
    const canManageGroup = amGroupAdmin || isEntityAdmin || isMasterAdmin;
    const canDeleteGroup = Boolean(groupInfo?.isCreator) || isEntityAdmin || isMasterAdmin;
    const hasLeft = Boolean(groupInfo?.hasLeft);
    const typingNames = [...typingUsers.values()];

    const header = groupSummary || {};
    const tone = avatarToneFor(header.name);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', bgcolor: '#F6F7FF' }}>
            {selectMode ? (
                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', px: 1.6, py: 1.1, bgcolor: '#fff', borderBottom: '1px solid #EEF0F6' }}>
                    <IconButton size="small" onClick={exitSelectMode}><CloseRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A', flex: 1 }}>{selectedIds.size} selected</Typography>
                    <Tooltip arrow title="Delete selected">
                        <span>
                            <IconButton size="small" disabled={!selectedIds.size} onClick={() => setConfirmBulkDelete(true)} sx={{ color: selectedIds.size ? '#E11D48' : '#CBD2DD' }}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            ) : (
            <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', px: 1.6, py: 1.1, bgcolor: '#fff', borderBottom: '1px solid #EEF0F6' }}>
                {isMobile && (
                    <IconButton size="small" onClick={onBack}><ArrowBackRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
                )}
                {header.iconUrl ? (
                    <Avatar src={header.iconUrl} sx={{ width: 38, height: 38 }} />
                ) : (
                    <Avatar sx={{ width: 38, height: 38, ...tone, fontSize: 13, fontWeight: 800 }}>{initialsFrom(header.name)}</Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }} onClick={() => setInfoOpen(true)}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }} noWrap>{header.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: typingNames.length ? PRIMARY : '#94A3B8' }} noWrap>
                        {typingNames.length
                            ? `${typingNames.join(', ')} typing…`
                            : `${members.length || header.memberCount || 0} member${(members.length || header.memberCount) === 1 ? '' : 's'}`}
                    </Typography>
                </Box>
                <Tooltip arrow title="Search in this chat">
                    <IconButton size="small" onClick={() => setSearchOpen((o) => !o)} sx={{ color: searchOpen ? PRIMARY : '#94A3B8' }}><SearchRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
                </Tooltip>
                <Tooltip arrow title="Group info">
                    <IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ color: '#94A3B8' }}><InfoOutlinedIcon sx={{ fontSize: 19 }} /></IconButton>
                </Tooltip>
            </Stack>
            )}

            {searchOpen && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.6, py: 1, bgcolor: '#fff', borderBottom: '1px solid #EEF0F6' }}>
                    <SearchRoundedIcon sx={{ fontSize: 16, color: '#98A0AE' }} />
                    <InputBase autoFocus placeholder="Search messages in this group…" value={searchQuery} onChange={(e) => runSearch(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                    {searchResults.length > 0 && (
                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{searchIndex + 1}/{searchResults.length}</Typography>
                    )}
                    <IconButton size="small" disabled={!searchResults.length} onClick={() => jumpToSearchResult(-1)}><KeyboardArrowUpRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" disabled={!searchResults.length} onClick={() => jumpToSearchResult(1)}><KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); }}><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                </Stack>
            )}

            {pinnedMessage && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.6, py: 0.8, bgcolor: PRIMARY_LIGHT, borderBottom: '1px solid #E0F0FB' }}>
                    <PushPinRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                    <Typography sx={{ fontSize: 12, color: '#334155', flex: 1 }} noWrap>
                        <b>Pinned:</b> {pinnedMessage.messageText || pinnedMessage.attachmentName || 'Message'}
                    </Typography>
                    {canManageGroup && (
                        <IconButton size="small" onClick={() => togglePin(pinnedMessage)}><CloseRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                    )}
                </Stack>
            )}

            <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Box ref={scrollRef} onScroll={onScroll} sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', px: { xs: 1, sm: 2 }, py: 1.4 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><ChatLoader size={32} /></Box>
                ) : messages.length === 0 ? (
                    <EmptyState icon={GroupsRoundedIcon} title="No messages yet" hint="Say hello to get the conversation started." />
                ) : (
                    <>
                        {loadingOlder && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={16} /></Box>}
                        {messages.map((m, i) => {
                            const prev = messages[i - 1];
                            const dayChanged = !prev || formatDayDivider(prev.sentOn) !== formatDayDivider(m.sentOn);
                            const showDivider = unreadDividerIdRef.current === m.messageId;
                            const mine = m.senderUserId != null && m.senderUserId === myUserId;

                            if (m.messageType === 'system') {
                                return (
                                    <React.Fragment key={m.messageId}>
                                        {dayChanged && <DayDivider label={formatDayDivider(m.sentOn)} />}
                                        {showDivider && <UnreadDivider />}
                                        <Box id={`chat-msg-${m.messageId}`} sx={{ textAlign: 'center', my: 1 }}>
                                            <Typography component="span" sx={{ fontSize: 11.5, color: '#94A3B8', bgcolor: '#EEF0F6', borderRadius: '999px', px: 1.4, py: 0.4, display: 'inline-block' }}>
                                                {m.messageText || systemEventLabel(m.systemEventType)}
                                            </Typography>
                                        </Box>
                                    </React.Fragment>
                                );
                            }

                            const readByAll = !m.isTemp && readByAllUpTo >= m.messageId;
                            const canEdit = mine && !m.isTemp && canEditMessage(m);
                            const canDelete = !m.isTemp && !m.isDeleted && (mine || canManageGroup);
                            const canPin = !m.isTemp && !m.isDeleted && canManageGroup;
                            const isSelected = selectedIds.has(m.messageId);
                            const selectCheckbox = (
                                // The outer row Box (below) also toggles selection on click — stop
                                // propagation here so tapping the checkbox itself doesn't fire both
                                // and cancel back out to the original state.
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleSelected(m.messageId); }}>
                                    {isSelected
                                        ? <CheckCircleRoundedIcon sx={{ fontSize: 19, color: PRIMARY }} />
                                        : <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 19, color: '#B0B6C3' }} />}
                                </IconButton>
                            );

                            return (
                                <React.Fragment key={m.messageId}>
                                    {dayChanged && <DayDivider label={formatDayDivider(m.sentOn)} />}
                                    {showDivider && <UnreadDivider />}
                                    <Box
                                        id={`chat-msg-${m.messageId}`}
                                        onClick={selectMode && !m.isTemp ? () => toggleSelected(m.messageId) : undefined}
                                        sx={{
                                            display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', mb: 1.1,
                                            borderRadius: '10px', transition: 'background-color 0.4s',
                                            cursor: selectMode && !m.isTemp ? 'pointer' : 'default',
                                            bgcolor: isSelected ? PRIMARY_LIGHT : (highlightMsgId === m.messageId ? PRIMARY_LIGHT : 'transparent'),
                                        }}
                                    >
                                        {!mine && (
                                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: avatarToneFor(m.senderName).color, mb: 0.3, ml: 0.5 }}>{m.senderName}</Typography>
                                        )}
                                        <Stack direction="row" spacing={0.4} sx={{ alignItems: 'center', maxWidth: '78%' }}>
                                            {mine && !m.isTemp && (
                                                selectMode ? selectCheckbox : (
                                                    <MessageTools message={m} canEdit={canEdit} canDelete={canDelete} canPin={canPin}
                                                        onReply={() => setReplyTo(m)} onEdit={() => setEditingMessage(m)}
                                                        onDelete={() => deleteMessage(m)} onPin={() => togglePin(m)} onCopy={() => copyMessageText(m)}
                                                        onSelect={() => enterSelectMode(m.messageId)}
                                                        onReact={(e) => { setReactMoreOpen(false); setReactAnchor({ anchorEl: e.currentTarget, message: m }); }} />
                                                )
                                            )}
                                            <SwipeToReplyRow mine={mine} disabled={m.isDeleted || m.isTemp || selectMode} onReply={() => setReplyTo(m)}>
                                            <Box
                                                sx={{
                                                    bgcolor: m.isDeleted ? '#F1F1F5' : (mine ? PRIMARY : '#fff'),
                                                    color: m.isDeleted ? '#94A3B8' : (mine ? '#fff' : '#0F172A'),
                                                    border: mine || m.isDeleted ? 'none' : '1px solid #EEF0F6',
                                                    borderRadius: '12px', px: 1.4, py: 1, minWidth: 60,
                                                    boxShadow: mine ? 'none' : '0 1px 2px rgba(16,24,40,0.04)',
                                                    // In select mode, a tap anywhere on the row should just toggle
                                                    // selection — not also open an attachment link, react, etc.
                                                    pointerEvents: selectMode ? 'none' : 'auto',
                                                }}
                                            >
                                                {m.replyToMessageId && !m.isDeleted && (
                                                    <Box sx={{ borderLeft: `3px solid ${mine ? 'rgba(255,255,255,0.6)' : PRIMARY}`, pl: 1, mb: 0.6, opacity: 0.85 }}>
                                                        <Typography sx={{ fontSize: 10.5, fontWeight: 800 }}>{m.replyToSenderName}</Typography>
                                                        <Typography sx={{ fontSize: 11 }} noWrap>{m.replyToText}</Typography>
                                                    </Box>
                                                )}
                                                {m.isDeleted ? (
                                                    <Typography sx={{ fontSize: 12.5, fontStyle: 'italic' }}>This message was deleted</Typography>
                                                ) : (
                                                    <>
                                                        {m.attachmentUrl && <Attachment message={m} mine={mine} />}
                                                        {m.messageText && (
                                                            <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {linkify(m.messageText, mine ? '#DCE8FF' : '#1B74E4').map((part, idx) => (
                                                                    typeof part === 'string'
                                                                        ? <React.Fragment key={idx}>{part}</React.Fragment>
                                                                        : <Box key={part.key} component="a" href={part.href} target="_blank" rel="noopener noreferrer" sx={{ color: part.color, textDecoration: 'underline', wordBreak: 'break-all' }}>{part.text}</Box>
                                                                ))}
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end', mt: 0.4 }}>
                                                    {m.isEdited && !m.isDeleted && <Typography sx={{ fontSize: 9.5, opacity: 0.7 }}>edited</Typography>}
                                                    <Typography sx={{ fontSize: 9.5, opacity: 0.7 }}>{formatMessageTime(m.sentOn)}</Typography>
                                                    {mine && !m.isDeleted && (
                                                        m.isTemp ? (
                                                            <DoneRoundedIcon sx={{ fontSize: 13, opacity: 0.4 }} />
                                                        ) : (
                                                            <Box onClick={(e) => openReadInfo(e, m)} sx={{ cursor: 'pointer', display: 'flex' }}>
                                                                {readByAll
                                                                    ? <DoneAllRoundedIcon sx={{ fontSize: 13, color: '#9CD2FF' }} />
                                                                    : <DoneRoundedIcon sx={{ fontSize: 13, opacity: 0.7 }} />}
                                                            </Box>
                                                        )
                                                    )}
                                                </Stack>
                                                {Array.isArray(m.reactions) && m.reactions.length > 0 && (
                                                    <Stack direction="row" spacing={0.4} sx={{ mt: 0.6, flexWrap: 'wrap', gap: 0.4 }}>
                                                        {m.reactions.map((r) => (
                                                            <Box
                                                                key={r.emoji}
                                                                onClick={(e) => setWhoReacted({ anchorEl: e.currentTarget, reaction: r })}
                                                                sx={{ cursor: 'pointer', bgcolor: mine ? 'rgba(255,255,255,0.22)' : '#E4F0F9', borderRadius: '999px', px: 0.8, py: 0.1, fontSize: 11 }}
                                                            >
                                                                {r.emoji} {r.count}
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </Box>
                                            </SwipeToReplyRow>
                                            {!mine && (
                                                selectMode ? selectCheckbox : (
                                                    <MessageTools message={m} canEdit={false} canDelete={canDelete} canPin={canPin}
                                                        onReply={() => setReplyTo(m)} onEdit={() => {}}
                                                        onDelete={() => deleteMessage(m)} onPin={() => togglePin(m)} onCopy={() => copyMessageText(m)}
                                                        onSelect={() => enterSelectMode(m.messageId)}
                                                        onReact={(e) => { setReactMoreOpen(false); setReactAnchor({ anchorEl: e.currentTarget, message: m }); }} />
                                                )
                                            )}
                                        </Stack>
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                    </>
                )}
            </Box>

            {(showScrollDown || newBelowCount > 0) && (
                <Box sx={{ position: 'absolute', right: 16, bottom: 16 }}>
                    <Tooltip arrow title="Scroll to latest">
                        <IconButton
                            onClick={scrollToBottomNow}
                            sx={{ width: 38, height: 38, bgcolor: '#fff', border: '1px solid #EEF0F6', boxShadow: '0 3px 10px rgba(16,24,40,0.14)', '&:hover': { bgcolor: '#F8F8FC' } }}
                        >
                            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 19, color: PRIMARY }} />
                        </IconButton>
                    </Tooltip>
                    {newBelowCount > 0 && (
                        <Box sx={{ position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, px: 0.5, borderRadius: '9px', bgcolor: PRIMARY, color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {newBelowCount > 99 ? '99+' : newBelowCount}
                        </Box>
                    )}
                </Box>
            )}
            </Box>

            {typingNames.length > 0 && (
                <Box sx={{ px: 2, py: 0.7, borderTop: '1px solid #EEF0F6', bgcolor: '#FAFAFF' }}>
                    <Typography sx={{ fontSize: 12, color: PRIMARY, fontWeight: 700, fontStyle: 'italic' }} noWrap>
                        {typingNames.join(', ')} {typingNames.length > 1 ? 'are' : 'is'} typing…
                    </Typography>
                </Box>
            )}

            <MessageComposer
                disabled={hasLeft}
                disabledReason="You are no longer a member of this group."
                sending={sending}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                editingMessage={editingMessage}
                onCancelEdit={() => setEditingMessage(null)}
                onSend={sendMessage}
                onSaveEdit={saveEdit}
                onTyping={handleTyping}
            />

            <Popover
                open={Boolean(reactAnchor)}
                anchorEl={reactAnchor?.anchorEl}
                onClose={() => { setReactAnchor(null); setReactMoreOpen(false); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {reactMoreOpen ? (
                    <EmojiPickerPanel onSelect={(emj) => react(reactAnchor.message, emj)} />
                ) : (
                    <Stack direction="row" spacing={0.5} sx={{ p: 0.8, alignItems: 'center' }}>
                        {QUICK_REACTIONS.map((emj) => (
                            <IconButton key={emj} size="small" onClick={() => react(reactAnchor.message, emj)} sx={{ fontSize: 18 }}>{emj}</IconButton>
                        ))}
                        <Tooltip arrow title="More reactions">
                            <IconButton size="small" onClick={() => setReactMoreOpen(true)} sx={{ bgcolor: '#F1F5F9', '&:hover': { bgcolor: '#E2E8F0' } }}>
                                <AddRoundedIcon sx={{ fontSize: 16, color: '#64748B' }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Popover>

            <Popover open={Boolean(whoReacted)} anchorEl={whoReacted?.anchorEl} onClose={() => setWhoReacted(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Box sx={{ p: 1.2, minWidth: 140 }}>
                    <Typography sx={{ fontSize: 16, mb: 0.6 }}>{whoReacted?.reaction.emoji} {whoReacted?.reaction.count}</Typography>
                    {(whoReacted?.reaction.userIds || []).map((uid) => {
                        const m = members.find((mm) => mm.userId === uid);
                        return <Typography key={uid} sx={{ fontSize: 12, color: '#334155' }}>{m?.name || `User ${uid}`}</Typography>;
                    })}
                </Box>
            </Popover>

            <Popover open={Boolean(readInfoAnchor)} anchorEl={readInfoAnchor} onClose={() => setReadInfoAnchor(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Box sx={{ p: 1.4, minWidth: 180, maxWidth: 240 }}>
                    {readInfo?.loading ? <CircularProgress size={16} /> : (
                        <>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#98A0AE', textTransform: 'uppercase', mb: 0.6 }}>Read by</Typography>
                            {(readInfo?.data?.readBy || readInfo?.data?.items || []).length === 0 ? (
                                <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>Not read yet.</Typography>
                            ) : (
                                (readInfo?.data?.readBy || readInfo?.data?.items || []).map((r, idx) => (
                                    <Typography key={r.userId || idx} sx={{ fontSize: 12, color: '#334155' }}>{r.name || r.userName}</Typography>
                                ))
                            )}
                        </>
                    )}
                </Box>
            </Popover>

            <GroupInfoPanel
                open={infoOpen}
                onClose={() => setInfoOpen(false)}
                group={groupInfo ? { ...groupSummary, ...groupInfo, members } : groupSummary}
                myUserId={myUserId}
                canManage={canManageGroup}
                canDelete={canDeleteGroup}
                onManageMembers={() => { setInfoOpen(false); setManageOpen(true); }}
                onLeave={() => { setInfoOpen(false); leaveGroup(); }}
                onDelete={() => { setInfoOpen(false); deleteGroup(); }}
                onClearChat={() => { setInfoOpen(false); clearChat(); }}
                onGroupChanged={() => { onGroupsListChanged?.(); }}
            />
            <ManageMembersDialog
                open={manageOpen}
                onClose={() => setManageOpen(false)}
                group={groupInfo}
                currentMembers={members}
                onSaved={() => {
                    http.get(GetGroupInfo, { params: { groupId } }).then(({ data: body }) => { if (!body?.error) setGroupInfo(body?.data || null); }).catch(() => {});
                    onGroupsListChanged?.();
                }}
            />
            <ConfirmDialog
                open={confirmBulkDelete}
                onClose={() => setConfirmBulkDelete(false)}
                onConfirm={bulkDeleteSelected}
                title={`Delete ${selectedIds.size} message${selectedIds.size === 1 ? '' : 's'}?`}
                body="This can't be undone."
                confirmLabel="Delete"
            />
        </Box>
    );
}

function DayDivider({ label }) {
    return (
        <Box sx={{ textAlign: 'center', my: 1.4 }}>
            <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: '#94A3B8', bgcolor: '#EEF0F6', borderRadius: '999px', px: 1.2, py: 0.35 }}>{label}</Typography>
        </Box>
    );
}

function UnreadDivider() {
    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', my: 1.2 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#FCA5A5' }} />
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#E11D48' }}>Unread messages</Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#FCA5A5' }} />
        </Stack>
    );
}

function MessageTools({ message, canEdit, canDelete, canPin, onReply, onEdit, onDelete, onPin, onCopy, onReact, onSelect }) {
    const [anchor, setAnchor] = useState(null);
    const canCopy = !message.isDeleted && Boolean(message.messageText);
    return (
        <Stack direction="row" sx={{ opacity: 0.55, transition: 'opacity .12s', '&:hover': { opacity: 1 } }}>
            {!message.isDeleted && (
                <>
                    <Tooltip arrow title="React"><IconButton size="small" onClick={onReact}><AddReactionOutlinedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    <Tooltip arrow title="Reply"><IconButton size="small" onClick={onReply}><ReplyRoundedIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                </>
            )}
            {/* Always available, even on a deleted message — mainly so it can
                still be picked up by multi-select (e.g. to clear a run of
                deleted placeholders); the other items just don't apply then. */}
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVertRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                {canCopy && <MenuItem onClick={() => { setAnchor(null); onCopy(); }}><ContentCopyRoundedIcon sx={{ fontSize: 16, mr: 1 }} />Copy</MenuItem>}
                {canEdit && <MenuItem onClick={() => { setAnchor(null); onEdit(); }}><EditRoundedIcon sx={{ fontSize: 16, mr: 1 }} />Edit</MenuItem>}
                {canPin && <MenuItem onClick={() => { setAnchor(null); onPin(); }}><PushPinRoundedIcon sx={{ fontSize: 16, mr: 1 }} />{message.isPinned ? 'Unpin' : 'Pin'}</MenuItem>}
                <MenuItem onClick={() => { setAnchor(null); onSelect(); }}><PlaylistAddCheckRoundedIcon sx={{ fontSize: 16, mr: 1 }} />Select</MenuItem>
                {canDelete && <MenuItem onClick={() => { setAnchor(null); onDelete(); }}><DeleteOutlineRoundedIcon sx={{ fontSize: 16, mr: 1, color: '#E11D48' }} /><Box component="span" sx={{ color: '#E11D48' }}>Delete</Box></MenuItem>}
            </Menu>
        </Stack>
    );
}
